import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, Ban, CheckCircle2, ShieldPlus, ShieldMinus, Trash2, Zap, MoreHorizontal } from "lucide-react";
import { PageTitle, Table, Td, fmtDate } from "../../components/admin/AdminUi";
import { api, errMsg } from "../../lib/api";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";
import AddCreditDialog from "../../components/admin/credits/AddCreditDialog";

const FILTERS = ["all", "admin", "blocked"];

export default function AdminUsers() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [grant, setGrant] = useState(null);
  const load = useCallback(() => api.adminUsers({ q, filter }).then(setRows).catch(() => {}), [q, filter]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const act = async (fn, msg) => { try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); } };
  const del = (u) => { if (window.confirm(`Delete ${u.email} and all their projects?`)) act(() => api.adminDeleteUser(u.id), "User deleted"); };

  return (
    <div data-testid="admin-users">
      <PageTitle title="Users" desc={`${rows.length} shown`} right={
        <div className="flex items-center gap-2">
          <div className="flex bg-slate-100 border border-slate-200 rounded-lg p-0.5">
            {FILTERS.map((f) => <button key={f} onClick={() => setFilter(f)} data-testid={`users-filter-${f}`} className={`h-7 px-3 rounded-md text-xs capitalize ${filter === f ? "bg-slate-200 text-slate-900" : "text-slate-500"}`}>{f}</button>)}
          </div>
          <div className="relative"><Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search" data-testid="users-search-input" className="h-8 w-48 pl-8 pr-3 rounded-lg bg-slate-100 border border-slate-200 text-sm text-slate-900 outline-none" /></div>
        </div>
      } />
      <Table head={["User", "Credits", "Projects", "Joined", "Status", ""]} testid="users-table" empty={rows.length === 0 && "No users found."}>
        {rows.map((u) => (
          <tr key={u.id} data-testid={`user-row-${u.id}`} className="hover:bg-slate-50">
            <Td><div className="text-slate-900">{u.name}</div><div className="text-xs text-slate-500">{u.email}</div></Td>
            <Td className="font-mono text-xs"><span className="text-emerald-700">{u.credits_free + u.credits_paid}</span> <span className="text-slate-400">({u.credits_free} free · {u.credits_paid} paid)</span></Td>
            <Td className="font-mono">{u.projects}</Td>
            <Td className="text-xs text-slate-500 whitespace-nowrap">{fmtDate(u.created_at)}</Td>
            <Td>
              {u.is_admin && <span className="mr-1 px-1.5 py-0.5 rounded text-[10px] font-mono bg-amber-50 border border-amber-200 text-amber-600">admin</span>}
              {u.is_blocked ? <span className="px-1.5 py-0.5 rounded text-[10px] font-mono bg-rose-50 border border-rose-200 text-rose-600">blocked</span> : <span className="text-xs text-slate-500">active</span>}
            </Td>
            <Td className="text-right">
              <DropdownMenu>
                <DropdownMenuTrigger asChild><button data-testid={`user-actions-${u.id}`} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500"><MoreHorizontal size={16} /></button></DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-white border-slate-200">
                  <DropdownMenuItem onSelect={() => setGrant(u)} data-testid={`user-grant-${u.id}`} className="gap-2"><Zap size={13} /> Add credit</DropdownMenuItem>
                  {u.is_blocked
                    ? <DropdownMenuItem onSelect={() => act(() => api.adminUnblockUser(u.id), "Unblocked")} className="gap-2"><CheckCircle2 size={13} /> Unblock</DropdownMenuItem>
                    : <DropdownMenuItem onSelect={() => act(() => api.adminBlockUser(u.id, window.prompt("Reason?") || ""), "Blocked")} data-testid={`user-block-${u.id}`} className="gap-2"><Ban size={13} /> Block</DropdownMenuItem>}
                  {u.is_admin
                    ? <DropdownMenuItem onSelect={() => act(() => api.adminDemote(u.id), "Admin removed")} className="gap-2"><ShieldMinus size={13} /> Remove admin</DropdownMenuItem>
                    : <DropdownMenuItem onSelect={() => act(() => api.adminPromote(u.id), "Promoted to admin")} className="gap-2"><ShieldPlus size={13} /> Make admin</DropdownMenuItem>}
                  <DropdownMenuItem onSelect={() => del(u)} className="gap-2 text-rose-600"><Trash2 size={13} /> Delete</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </Td>
          </tr>
        ))}
      </Table>
      <AddCreditDialog open={!!grant} user={grant} onClose={() => setGrant(null)} onDone={load} />
    </div>
  );
}
